import { create, StoreApi, UseBoundStore } from "zustand";
import { persist } from "zustand/middleware";
import {
    getLSStore,
    LS_Store_Name,
    LS_VERSION,
    LSStoreType,
    PreferencesStoreProperties,
    removeLSStore,
    setLSStore,
} from "./utils/LS-Store";
import { RemoI18nLanguages, RemoI18nLanguagesEnum } from "@obsbot/remo-i18n";

export interface HotkeyItem {
    key: string;
    value: number[];
}

export interface Hotkeys {
    CopyText: HotkeyItem;
    PasteText: HotkeyItem;
    CreateChannel: HotkeyItem;

    Switch2Audio: HotkeyItem;
    Switch2Video: HotkeyItem;
    SwitchMic: HotkeyItem;
    CallEmoticons: HotkeyItem;
}

// 覆盖样式, 默认轮盘式以及标签式
export enum OverlayStyleType {
    Default = "Default",
    Tab = "Tab",
}
/**
 * 排列方向, 竖向以及横向
 * - Horizontal: 横向排列
 * - Vertical: 竖向排列
 */
export enum OverlayShowDirection {
    Horizontal = "Horizontal",
    Vertical = "Vertical",
}
// 样式设置的一些参数
export interface OverlaySetting {
    overlayStyle: OverlayStyleType;
    showDirection: OverlayShowDirection;
    overlaySize: number;
    overlayOpacity: number;
    position: {
        x?: number;
        y?: number;
    };
}
// 覆盖的显示设置
export enum OverlayDisplaySetting {
    Always = "Always",
    OnlyTalk = "OnlyTalk",
}
// 特效显示的类型
export enum ShowAnimationType {
    Default = "inApplication",
    Fullscreen = "fullApplication",
}

export enum VideoWindowLayoutTypes {
    Equal = "equal",
    Top = "top",
    Bottom = "bottom",
}

/**
 * 偏好设置存储状态接口
 */
export interface PreferencesStoreState {
    /**
     * 是否显示过频道新手引导
     */
    hasShowedChannelGuide: boolean | null;
    /**
     * 是否需要提示异常退出
     */
    exitRoomID: string;
    /**
     * 是否总是视频开黑
     */
    alwaysVideo: boolean;
    /**
     * 回声抵消
     */
    enabledEchoCancellation: boolean;
    /**
     * 麦克风降噪等级
     */
    noiseSuppressionLevel: number;
    /**
     * 麦克风自动增益
     */
    autoGainControl: boolean;
    /**
     * 在开启视频时, 总是预览摄像头
     */
    alwaysPreviewCam: boolean;
    /**
     * 画面降噪
     */
    enabledVideoDenoise: boolean;
    /**
     * 硬件加速
     */
    enabledHardwareAcceleration: boolean;
    /**
     * 热键列表
     */
    hotkeys: {
        [key in keyof Hotkeys]: HotkeyItem;
    };
    /**
     * 全屏显示游戏覆盖
     */
    fullscreenShowGameoverlay: boolean;
    /**
     * 桌面显示游戏覆盖
     */
    desktopShowGameoverlay: boolean;
    /**
     * 覆盖样式设置
     */
    overlaySetting: OverlaySetting;
    /**
     * 覆盖显示设置
     */
    overlayDisplaySetting: OverlayDisplaySetting;
    /**
     * 显示的最大用户数
     */
    maxShowUser: number;
    /**
     * 特效显示的类型
     */
    showAnimationType: ShowAnimationType;
    /**
     * 展示的特效
     */
    showAnimations: string[] | undefined;
    /**
     * 语言选择
     */
    language: RemoI18nLanguages;
    /**
     * 视频通话布局偏好
     */
    channelLayoutType: VideoWindowLayoutTypes;
    /**
     * 视频镜像
     */
    videoMirror: boolean;
    /**
     * 更新是否显示过频道引导
     */
    updateShowChannelGuide: (hasShowedChannelGuide: boolean | null) => void;
    /**
     * 更新异常退出的房间号
     */
    updateShowExitRoomID: (exitRoomID: string) => void;
    /**
     * 更新总是视频游戏开黑
     * @param enabled - 是否启用总是视频游戏开黑
     */
    updateAlwaysVideo: (enabled: boolean) => void;
    /**
     * 更新回声抵消状态
     * @param enabled - 回声抵消是否启用
     */
    updateEnabledEchoCancellation: (enabled: boolean) => void;
    /**
     * 更新麦克风降噪等级
     * @param level - 麦克风降噪等级
     */
    updateNoiseSuppressionLevel: (level: number) => void;
    /**
     * 更新麦克风自动增益状态
     * @param enabled - 麦克风自动增益是否启用
     */
    updateAutoGainControl: (enabled: boolean) => void;
    /**
     * 更新在开启视频时总是预览摄像头的状态
     * @param enabled - 在开启视频时是否总是预览摄像头
     */
    updateAlwaysPreviewCam: (enabled: boolean) => void;
    /**
     * 更新画面降噪状态
     * @param enabled - 画面降噪是否启用
     */
    updateEnabledVideoDenoise: (enabled: boolean) => void;
    /**
     * 更新硬件加速状态
     * @param enabled - 硬件加速是否启用
     */
    updateEnabledHardwareAcceleration: (enabled: boolean) => void;
    /**
     * 更新热键列表
     * @param hotkeys - 新的热键列表
     */
    updateHotkeys: (hotkeys: Hotkeys) => void;
    /**
     * 更新游戏全屏显示游戏覆盖
     * @param fullscreenOverlay - 是否启用游戏全屏显示游戏覆盖
     */
    updateFullscreenOverlay: (fullscreenOverlay: boolean) => void;
    /**
     * 更新桌面显示游戏覆盖
     * @param desktopOverlay - 是否启用系统桌面显示游戏覆盖
     */
    updateDesktopOverlay: (desktopOverlay: boolean) => void;
    /**
     * 更新覆盖样式设置
     * @param overlaySetting - 覆盖样式设置
     */
    updateOverlaySetting: (overlaySetting: OverlaySetting) => void;
    /**
     * 更新覆盖显示设置
     * @param overlayDisplaySetting - 覆盖显示设置
     */
    updateOverlayDisplaySetting: (overlayDisplaySetting: OverlayDisplaySetting) => void;
    /**
     * 更新最大显示的用户数
     * @param maxShowUser - 最大显示的用户数
     */
    updateMaxShowUser: (maxShowUser: number) => void;
    /**
     * 更新特效显示的类型
     * @param showAnimationType - 特效显示的类型
     */
    updateShowAnimationType: (showAnimationType: ShowAnimationType) => void;
    /**
     * 更新特效列表
     * @param showAnimations - 特效列表
     */
    updateShowAnimations: (showAnimations: string[]) => void;
    /**
     * 更新语言偏好
     * @param language - 选择的语言
     */
    updateLanguage: (language: RemoI18nLanguages) => void;
    /**
     * 更新视频通话布局偏好
     * @param channelLayoutType - 视频通话布局偏好
     */
    updateChannelLayoutType: (channelLayoutType: VideoWindowLayoutTypes) => void;
    /**
     * 更新视频镜像
     * @param videoMirror - 是否启用视频镜像
     */
    updateVideoMirror: (videoMirror: boolean) => void;
}

export const defaultHotkeys = {
    CopyText: {
        key: "Ctrl+C",
        value: [162, 67],
    },
    PasteText: {
        key: "Ctrl+V",
        value: [162, 86],
    },
    CreateChannel: {
        key: "Ctrl+Shift+N",
        value: [162, 160, 78],
    },
    Switch2Audio: {
        key: "Shift+A",
        value: [160, 65],
    },
    Switch2Video: {
        key: "Shift+V",
        value: [160, 86],
    },
    SwitchMic: {
        key: "Shift+M",
        value: [160, 77],
    },
    CallEmoticons: {
        key: "Ctrl+Shift+T",
        value: [162, 160, 84],
    },
};

const storeMap = new Map<string, UseBoundStore<StoreApi<PreferencesStoreState>>>();
export const createPreferencesStore = (
    uid: string,
): UseBoundStore<StoreApi<PreferencesStoreState>> => {
    if (!storeMap.has(uid)) {
        const store = create<PreferencesStoreState>()(
            persist(
                set => ({
                    hasShowedChannelGuide: false,
                    exitRoomID: "",
                    alwaysVideo: true,
                    enabledEchoCancellation: true,
                    noiseSuppressionLevel: 2,
                    autoGainControl: true,
                    alwaysPreviewCam: true,
                    enabledVideoDenoise: true,
                    enabledHardwareAcceleration: true,
                    fullscreenShowGameoverlay: true,
                    desktopShowGameoverlay: false,
                    channelLayoutType: VideoWindowLayoutTypes.Equal,
                    videoMirror: true,
                    hotkeys: defaultHotkeys,
                    overlaySetting: {
                        overlayStyle: OverlayStyleType.Default,
                        showDirection: OverlayShowDirection.Vertical,
                        overlaySize: 50,
                        overlayOpacity: 50,
                        position: {},
                    },
                    overlayDisplaySetting: OverlayDisplaySetting.Always,
                    maxShowUser: 5,
                    showAnimationType: ShowAnimationType.Default,
                    showAnimations: ["BombIcon", "WelcomeIcon", "CryIcon"],
                    language: RemoI18nLanguagesEnum.zhCN,
                    updateShowChannelGuide(hasShowedChannelGuide) {
                        set({ hasShowedChannelGuide });
                    },
                    updateShowExitRoomID(exitRoomID) {
                        set({ exitRoomID });
                    },
                    updateAlwaysVideo(enabled) {
                        set({ alwaysVideo: enabled });
                    },
                    updateEnabledEchoCancellation(enabled) {
                        set({ enabledEchoCancellation: enabled });
                    },
                    updateNoiseSuppressionLevel(level) {
                        set({ noiseSuppressionLevel: level });
                    },
                    updateAutoGainControl(enabled) {
                        set({ autoGainControl: enabled });
                    },
                    updateAlwaysPreviewCam(enabled) {
                        set({ alwaysPreviewCam: enabled });
                    },
                    updateEnabledVideoDenoise(enabled) {
                        set({ enabledVideoDenoise: enabled });
                    },
                    updateEnabledHardwareAcceleration(enabled) {
                        set({ enabledHardwareAcceleration: enabled });
                    },
                    updateHotkeys(hotkeys) {
                        (window as any).electron?.ipcRenderer.send(
                            "update-overlay-keydown-listener",
                            hotkeys,
                        );
                        set({ hotkeys });
                    },
                    updateDesktopOverlay(desktopOverlay) {
                        set({ desktopShowGameoverlay: desktopOverlay });
                    },
                    updateFullscreenOverlay(fullscreenOverlay) {
                        set({ fullscreenShowGameoverlay: fullscreenOverlay });
                    },
                    updateOverlaySetting(overlaySetting) {
                        set({ overlaySetting });
                    },
                    updateOverlayDisplaySetting(overlayDisplaySetting) {
                        set({ overlayDisplaySetting });
                    },
                    updateMaxShowUser(maxShowUser) {
                        set({ maxShowUser });
                    },
                    updateShowAnimationType(showAnimationType) {
                        set({ showAnimationType });
                    },
                    updateShowAnimations(showAnimations) {
                        set({ showAnimations });
                    },
                    updateLanguage(language) {
                        set({ language });
                    },
                    updateChannelLayoutType(channelLayoutType) {
                        set({ channelLayoutType });
                    },
                    updateVideoMirror(videoMirror) {
                        set({ videoMirror });
                    },
                }),
                {
                    name: `Remo-Preferences-Store-${uid}`,
                    version: LS_VERSION,
                },
            ),
        );
        storeMap.set(uid, store);
    }
    return storeMap.get(uid)!;
};

export const usePreferencesStore = (uid: string): UseBoundStore<StoreApi<PreferencesStoreState>> =>
    createPreferencesStore(uid)!;

export const getLSPreferencesStore = (
    uid: string,
): LSStoreType<PreferencesStoreProperties> | null =>
    getLSStore(LS_Store_Name.Preferences + "-" + uid);

export const setLSPreferencesStore = (value: LSStoreType<PreferencesStoreProperties>): void =>
    setLSStore(LS_Store_Name.Preferences, value);

export const removeLSPreferencesStore = (): void => removeLSStore(LS_Store_Name.Preferences);

export const getPreferencesStore = (uid: string): PreferencesStoreState =>
    // eslint-disable-next-line react-hooks/rules-of-hooks
    usePreferencesStore(uid).getState();
